import { useId, useRef, useState } from 'react';
import ChartAxisLabels from '@/components/feature/ChartAxisLabels';
import ChartGuides from '@/components/feature/ChartGuides';
import { useChartKeyboard } from '@/hooks/useChartKeyboard';
import { buildTicks, buildYearDividers, formatPercentTick, niceStep } from '@/utils/chartAxis';
import type { DrawdownPoint } from '@/utils/weeklyEquity';

interface DrawdownCurveProps {
  data: DrawdownPoint[];
  tone?: 'light' | 'dark';
  height?: number;
  pointNoun?: string;
  totalCount?: number;
}

export default function DrawdownCurve({ data, tone = 'light', height = 150, pointNoun = 'Week', totalCount }: DrawdownCurveProps) {
  const isDark = tone === 'dark';
  const gradientId = useId();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [active, setActive] = useState<number | null>(null);

  const n = data?.length ?? 0;
  const handleKeyDown = useChartKeyboard(n, setActive);

  if (!data || data.length === 0) {
    return null;
  }

  const setActiveFromClientX = (clientX: number) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0) return;
    const ratio = (clientX - rect.left) / rect.width;
    const index = Math.min(n - 1, Math.max(0, Math.round(ratio * (n - 1))));
    setActive(index);
  };
  /* Label a month only when it changes; thin them out when the axis gets busy. */
  const monthChangeOrder = data.reduce<number[]>((acc, point, i) => {
    if (i === 0 || point.month !== data[i - 1].month) acc.push(i);
    return acc;
  }, []);
  const axisStride = monthChangeOrder.length > 12 ? Math.ceil(monthChangeOrder.length / 12) : 1;
  const labeledIndices = new Set(
    monthChangeOrder.filter((_, order) => order % axisStride === 0),
  );
  const values = data.map((d) => d.value);
  const worst = Math.min(...values, 0);
  const depth = Math.abs(worst) || 1;
  const step = niceStep(depth, 3);
  const bottom = -Math.ceil(depth / step) * step;
  const ticks = buildTicks(bottom, 0, step);
  const span = -bottom || 1;
  const xPct = (i: number) => (n === 1 ? 50 : (i / (n - 1)) * 100);
  const topPct = (v: number) => (1 - (v - bottom) / span) * 100;

  const coords = data.map((d, i) => ({
    x: (xPct(i) / 100) * 1000,
    y: (topPct(d.value) / 100) * 100,
  }));
  const linePoints = coords.map((c) => `${c.x},${c.y}`).join(' ');
  const areaPath = `M0,0 L${linePoints.split(' ').join(' L')} L1000,0 Z`;

  const markerIndex = active ?? n - 1;
  const marker = data[markerIndex];
  const markerLeft = xPct(markerIndex);
  const markerTop = topPct(marker.value);
  const tooltipLeft = Math.min(94, Math.max(6, markerLeft));

  const gridColor = isDark ? 'bg-background-50/10' : 'bg-background-200/70';
  const axisTextColor = isDark ? 'text-background-200/60' : 'text-foreground-400';

  return (
    <div className="w-full">
      <div className="flex gap-2 md:gap-3">
        {/* Y axis tick labels */}
        <div className="relative w-10 shrink-0 md:w-12" style={{ height: `${height}px` }}>
          {ticks.map((tick) => (
            <span
              key={tick}
              className={`absolute right-0 -translate-y-1/2 whitespace-nowrap font-num text-[10px] font-medium ${axisTextColor}`}
              style={{ top: `${topPct(tick)}%` }}
            >
              {formatPercentTick(tick, step < 1 ? 1 : 0)}
            </span>
          ))}
        </div>

        <div className="min-w-0 flex-1">
          <div ref={containerRef} className="relative w-full" style={{ height: `${height}px` }}>
            {/* gridlines */}
            {ticks.map((tick) => (
              <div
                key={tick}
                className={`absolute left-0 right-0 h-px ${gridColor}`}
                style={{ top: `${topPct(tick)}%` }}
                aria-hidden="true"
              ></div>
            ))}

            {/* peak reference line */}
            <div
              className={`absolute left-0 right-0 h-px ${isDark ? 'bg-background-50/25' : 'bg-secondary-300'}`}
              style={{ top: '0%' }}
              aria-hidden="true"
            ></div>

            {/* area hanging below the peak */}
            <svg
              className={`absolute inset-0 w-full h-full ${isDark ? 'text-secondary-300' : 'text-secondary-500'}`}
              viewBox="0 0 1000 100"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="currentColor" stopOpacity="0.28" />
                  <stop offset="100%" stopColor="currentColor" stopOpacity="0.04" />
                </linearGradient>
              </defs>
              <path d={areaPath} fill={`url(#${gradientId})`} />
              <polyline
                points={linePoints}
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinejoin="round"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
              />
            </svg>

            {/* year dividers, month ticks, hover crosshair */}
            <ChartGuides
              yearDividers={buildYearDividers(data, xPct)}
              ticks={[...labeledIndices].map((i) => xPct(i))}
              crosshair={active !== null ? xPct(active) : null}
              isDark={isDark}
              accent="secondary"
            />

            {/* active marker */}
            <div
              className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-1/2 transition-all duration-150"
              style={{ left: `${markerLeft}%`, top: `${markerTop}%` }}
              aria-hidden="true"
            >
              <span className="block w-2.5 h-2.5 rounded-full bg-secondary-600 ring-4 ring-secondary-500/20"></span>
            </div>

            {/* tooltip */}
            <div
              className={`pointer-events-none absolute z-20 rounded-md px-2.5 py-1.5 text-[11px] font-num font-semibold transition-opacity whitespace-nowrap ${
                active !== null ? 'opacity-100' : 'opacity-0'
              } ${isDark ? 'bg-background-50 text-foreground-950' : 'bg-primary-950 text-background-50'}`}
              style={{ left: `${tooltipLeft}%`, top: `${markerTop}%`, transform: 'translate(-50%, 40%)' }}
            >
              <span className="block text-secondary-300">{marker.value.toFixed(2)}%</span>
              <span className={`block text-[10px] font-normal ${isDark ? 'text-foreground-500' : 'text-background-200/70'}`}>
                {marker.label}
              </span>
              <span className={`block text-[10px] font-normal ${isDark ? 'text-foreground-500' : 'text-background-200/70'}`}>
                {pointNoun} {marker.seq} of {totalCount ?? data.length}
              </span>
              <span className={`block text-[10px] font-normal ${isDark ? 'text-foreground-500' : 'text-background-200/70'}`}>
                peak {marker.peak.toLocaleString('en-US', { maximumFractionDigits: 0 })} USC
              </span>
            </div>

            {/* hover targets */}
            <div
              className="absolute inset-0 flex rounded-md outline-none focus-visible:ring-2 focus-visible:ring-accent-400"
              role="img"
              aria-label="Drawdown from peak curve — use the left and right arrow keys to step through each data point"
              tabIndex={0}
              onKeyDown={handleKeyDown}
              onMouseLeave={() => setActive(null)}
              onTouchStart={(event) => setActiveFromClientX(event.touches[0]?.clientX ?? 0)}
              onTouchMove={(event) => setActiveFromClientX(event.touches[0]?.clientX ?? 0)}
            >
              {data.map((point, i) => (
                <div
                  key={`${point.label}-${i}`}
                  className="flex-1 h-full cursor-pointer"
                  onMouseEnter={() => setActive(i)}
                ></div>
              ))}
            </div>
          </div>

          <ChartAxisLabels
            points={data}
            visibleIndices={labeledIndices}
            active={active}
            isDark={isDark}
            leftPct={xPct}
          />
        </div>
      </div>
    </div>
  );
}